import { reportRawClientErrorAsync } from "@/lib/reportClientError";

const ContractErrorMap: Record<string, string> = {
  "User rejected the request": "toast.chainError.userRejected",
  ECDSAInvalidSignature: "toast.chainError.ECDSAInvalidSignature",
  SignatureAlreadyUsed: "toast.chainError.signatureAlreadyUsed",
  SignatureExpired: "toast.chainError.signatureExpired",
  OrderAlreadyProcessed: "toast.chainError.orderAlreadyProcessed",
  WrongNonce: "toast.chainError.wrongNonce",
  SlippageExceeded: "toast.chainError.slippageExceeded",
  TokenNotSupported: "toast.chainError.tokenNotSupported",
  InvalidRecipient: "toast.chainError.invalidRecipient",
  AgentAlreadyActivated: "toast.chainError.agentAlreadyActivated",
  InvalidSignature: "toast.chainError.ECDSAInvalidSignature",
};
const ContractErrorKeys = Object.keys(ContractErrorMap);

/**
 * 将链上交互错误解析为 i18n key（`toast.chainError.*`，见各语言 `locales/*.json`）。
 * 入口处异步上报原始 error，不做匹配处理。
 *
 * 展示：`toast.error(i18next.t(parseChainError(err)))`
 */
export function parseChainError(
  error: unknown,
  fallbackKey = "toast.chainError.transactionError"
): string {
  reportRawClientErrorAsync(error);

  try {
    if (!error) {
      return fallbackKey;
    }
    if (error && typeof error === "object" && "name" in (error as object)) {
      const name = (error as { name: string }).name;
      switch (name) {
        case "UserRejectedRequestError":
          return "toast.chainError.userRejected";
        case "ContractFunctionExecutionError": {
          const shortMessage = (error as { message?: string })?.message || "";
          if (
            shortMessage.includes("insufficient funds") ||
            shortMessage.includes("exceeds the balance") ||
            shortMessage.includes("insufficient balance")
          ) {
            return "toast.chainError.insufficientBalance";
          }
          const matched = ContractErrorKeys.find(key =>
            shortMessage.includes(key)
          );
          if (matched) return ContractErrorMap[matched];
          return "toast.chainError.contractExecution";
        }
        case "TransactionExecutionError":
          return "toast.chainError.transactionExecution";
        default:
          return fallbackKey;
      }
    }

    if (typeof error === "string") {
      const lowerError = error.toLowerCase();
      if (
        lowerError.includes("user rejected") ||
        lowerError.includes("user denied")
      ) {
        return "toast.chainError.userRejected";
      }
      if (
        lowerError.includes("insufficient") ||
        lowerError.includes("balance")
      ) {
        return "toast.chainError.insufficientBalance";
      }
    }

    return fallbackKey;
  } catch {
    return fallbackKey;
  }
}
